import type { ICarUsedAdView } from "@finn-no/adview-provider-frontend-types";
import { EmbeddedPodlet } from "@mobility-item-page/client";
import { isAuction, isPrivateAdvertiser } from "@mobility-item-page/utilities";
import { AdPlacement } from "@schibsted-nmp/advertising-companion";
import { useAdViewMeta } from "src/context/AdViewProviderContext.tsx";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import type { AdProps } from "../components/Ad.tsx";
import AdReport from "../components/AdReport.tsx";
import {
  AdvertisingContainer,
  FinancingAdvertisingPlacement1,
  MobileOnlyFinancingAdvertisingPlacements,
} from "../components/advertising/AdvertisingPlacement.tsx";
import ComplaintClaim from "../components/complaint-claim/ComplaintClaim.tsx";
import { DesktopCompanyProfile, EnabledDealerOverview, MobileCTAs, NoDealerBorder } from "../components/Dealer.tsx";
import Description from "../components/Description.tsx";
import Disclaimer from "../components/Disclaimer.tsx";
import { Equipment } from "../components/Equipment.tsx";
import { EvInfoSection } from "../components/ev/EvInfoSection.tsx";
import { FeedbackForm } from "../components/FeedbackForm.tsx";
import { FinanceSection } from "../components/finance-section/FinanceSection.tsx";
import { Layout } from "../components/Layout.tsx";
import LocationSection from "../components/location/LocationSection.tsx";
import Price from "../components/Price.tsx";
import { PrivateSeller } from "../components/PrivateSeller.tsx";
import SearchLinks from "../components/SearchLinks.tsx";
import { SelfDeclaration } from "../components/SelfDeclaration.tsx";
import SafetyElements from "../components/safety-elements/Index.tsx";
import { Summary } from "../components/section-summary/SectionSummary.tsx";
import { CarSpecifications } from "../components/specification/car/CarSpecifications.tsx";
import Title from "../components/Title.tsx";
import UsefulResources from "../components/UsefulResources.tsx";
import { SignupAlert } from "../components/user-panel-signup/SignupAlert.tsx";
import { usePodlets } from "../podlets.ts";

type Props = AdProps & {
  ad: ICarUsedAdView;
};

export const CarUsed = ({ ad, safetyElements }: Props) => {
  const { isPreview, isMobile, enableFeedback } = useAppConfig();
  const { smoothTrade, transactionHubActiveProcess, motorInsight } = usePodlets();
  const { ownerUrn } = useAdViewMeta();
  const hasSmoothTrade = !!smoothTrade && !!smoothTrade.content;
  const isPrivateAd = isPrivateAdvertiser(ownerUrn);

  return (
    <Layout
      top={isPrivateAd ? <EmbeddedPodlet className="mb-40" response={transactionHubActiveProcess} /> : null}
      left={
        <>
          <Title title={ad.title} subTitle={ad.model_spec} statusText={ad.disposed_text} />
          <Summary ad={ad} />
          {!hasSmoothTrade && <Price />}
          <MobileCTAs />
          <FinancingAdvertisingPlacement1 />
          {isMobile && <MobileOnlyFinancingAdvertisingPlacements />}
        </>
      }
      leftBottom={
        <>
          <FinanceSection />
          {!isPreview && (
            <EmbeddedPodlet className="mt-40" response={motorInsight} cautiouslyReloadScriptOnHydrationError />
          )}
          <Description
            description={ad.description_unsafe}
            loanWarningLink={ad.loan_warning_link}
            carOfTheYear={ad.usedCarOfTheYear}
            carModelTaxonomy={ad.car_model_taxonomy}
            year={ad.year}
            modelSpec={ad.model_spec}
            fuelType={ad.engine?.fuel}
          />
          {isMobile && <AdvertisingContainer placement={AdPlacement.AdvtMiddle2} className="mt-40" />}
          <EvInfoSection />
          <CarSpecifications ad={ad} />
          <Equipment equipment={ad.equipment} />
          <SafetyElements safetyElements={safetyElements} />
          <SelfDeclaration ad={ad} />
          <ComplaintClaim hideClaim={isAuction(ad.sales_form)} />
          <LocationSection />
          {isMobile && <AdvertisingContainer placement={AdPlacement.AdvtMiddle2} className="mt-40" />}
          {isMobile && <UsefulResources />}
          {isMobile && <SignupAlert adViewType={ad.adViewType} />}
          <NoDealerBorder />
        </>
      }
      right={
        <>
          {isPrivateAd && <PrivateSeller adviewtype={ad.adViewType} />}
          <DesktopCompanyProfile />
          {!isMobile && <UsefulResources />}
          {!isMobile && <SignupAlert adViewType={ad.adViewType} />}
          {!isMobile && <AdvertisingContainer placement={AdPlacement.AdvtMiddle1} className={"mt-24"} />}
          {!isMobile && <AdvertisingContainer placement={AdPlacement.AdvtArticle1} className={"mt-24"} />}
          {!isMobile && <AdvertisingContainer placement={AdPlacement.AdvtArticle2} className={"mt-24"} />}
        </>
      }
      bottomWide={
        <>
          <EnabledDealerOverview />
          <AdReport />
          <Disclaimer />
          <SearchLinks />
          {enableFeedback && <FeedbackForm />}
        </>
      }
    />
  );
};
